import tailwindColors from "tailwindcss/colors"
import { NUMERIC_FIELD_KINDS } from "web/constants"
import { sortByKey } from "web/services/helpers"
import BarView from "../BarView/BarView"
import PieView from "../PieView/PieView"
import StackedBarView from "../StackedBarView/StackedBarView"
import { formatValue } from "web/services/format"

export const chartColours = [
  "#1e40af", // blue-800
  "#6366f1", // indigo-500
  "#0284c7", // sky-600
  "#0d9488", // teal-600
  "#10b981", // emerald-500
  "#84cc16", // lime-500
  "#06b6d4", // cyan-500
  "#7c3aed", // violet-600
  "#ef4444", // red-500
  "#f97316", // orange-500
  "#eab308", // yellow-500
  "#d946ef", // fuchsia-500
  "#db2777", // pink-600
  "#f43f5e", // rose-500
]

export const tooltipBorderColors = {
  blue: "border-blue-800",
  indigo: "border-indigo-500",
  sky: "border-sky-600",
  teal: "border-teal-600",
  emerald: "border-emerald-500",
  lime: "border-lime-500",
  cyan: "border-cyan-500",
  violet: "border-violet-600",
  red: "border-red-500",
  orange: "border-orange-500",
  yellow: "border-yellow-500",
  fuchsia: "border-fuchsia-500",
  pink: "border-pink-600",
  rose: "border-rose-500",
}

function findTailwindColorFromHex(hexCode) {
  const mappedTailWindColors = []

  Object.keys(tailwindColors)
    .filter(
      (k) =>
        k !== "current" &&
        k !== "warmGray" &&
        k !== "lightBlue" &&
        k !== "blueGray" &&
        k !== "coolGray" &&
        k !== "trueGray"
    )
    .forEach((name) => {
      const values = []
      Object.keys(tailwindColors[name]).forEach((key) => {
        values.push({
          name,
          key,
          hex: tailwindColors[name][key],
        })
      })
      mappedTailWindColors.push({ name, values })
    })

  const result = mappedTailWindColors
    .map((color) => {
      const found = color.values.find((item) => item.hex === hexCode)
      if (found) {
        return found
      }
      return false
    })
    .filter((color) => color)

  return result.length ? result[0].name : undefined
}

export function findTailwindBorderColorFromHex(hexCode) {
  const tailwindColor = findTailwindColorFromHex(hexCode)
  return tailwindColor ? tooltipBorderColors[tailwindColor] : hexCode
}

export const chartFormatter = (value, kind, unit, format = "Abbreviated") => {
  return formatValue({
    value,
    kind,
    unit,
    format,
  })
}

export const chartNegativeColours = [
  "hsl(003, 89%, 47%)",
  "hsl(000, 100%, 40%)",
  "hsl(016, 95%, 45%)",
  "hsl(003, 70%, 45%)",
  "hsl(013, 75%, 55%)",
  "hsl(007, 91%, 55%)",
  "hsl(018, 90%, 60%)",
  "hsl(004, 89%, 70%)",
  "hsl(012, 96%, 70%)",
  "hsl(001, 95%, 85%)",
  "hsl(016, 100%, 90%)",
]

export const WEIGHTED_BY_COUNT = {
  id: "__count",
  name: "__count",
  label: "Count",
  kind: "Number",
}

const GROUP_BY_FIELD_TYPES = [
  "Text",
  "Lookup",
  "Contact",
  "Company",
  "Portfolio",
  "User",
  "InternalContact",
]

export const CHART_TYPE = {
  BAR: "bar",
  STACKED_BAR: "stackedBar",
  PIE: "pie",
}

export const CHART_COMPONENT = {
  [CHART_TYPE.PIE]: PieView,
  [CHART_TYPE.BAR]: BarView,
  [CHART_TYPE.STACKED_BAR]: StackedBarView,
}

export const CHART_TYPES = [
  { value: CHART_TYPE.BAR, label: "Bar Chart" },
  { value: CHART_TYPE.PIE, label: "Pie Chart" },
]

export const getGroupFields = (fields) =>
  fields
    .filter((field) => GROUP_BY_FIELD_TYPES.includes(field.kind))
    .map((field) => ({ value: field.id, label: field.label }))

export const getWeightFields = (fields) =>
  fields
    .filter((field) => NUMERIC_FIELD_KINDS.includes(field.kind))
    .map((field) => ({ value: field.id, label: field.label }))

export const getChartFields = (fields) => [
  {
    id: "kind",
    key: "kind",
    name: "kind",
    kind: "Lookup",
    format: "Radio",
    label: "Chart Type",
    required: true,
    lookup_values: CHART_TYPES,
  },
  {
    id: "groupBy",
    key: "groupBy",
    name: "groupBy",
    label: "Group Field",
    kind: "Lookup",
    required: true,
    lookup_values: sortByKey(getGroupFields(fields), "label"),
  },
  {
    id: "weightedBy",
    key: "weightedBy",
    name: "weightedBy",
    label: "Value Field",
    kind: "Lookup",
    required: true,
    lookup_values: sortByKey(getWeightFields(fields), "label"),
  },
]
